/**
 * <js-scroll-strip> — a horizontally scrolling strip with previous/next controls.
 *
 * Expected markup:
 *   <js-scroll-strip>
 *     <div data-strip-track> ...items... </div>
 *     <button data-strip-prev></button>
 *     <button data-strip-next></button>
 *   </js-scroll-strip>
 *
 * The controls hide themselves when the track cannot scroll.
 */
class JsScrollStrip extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-strip-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-strip-prev]');
    this.next = this.querySelector('[data-strip-next]');
    this.loop = this.getAttribute('loop') !== 'false';

    this.handlePrev = () => this.scrollByStep(-1);
    this.handleNext = () => this.scrollByStep(1);
    this.update = () => this.updateControls();

    this.prev?.addEventListener('click', this.handlePrev);
    this.next?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.update, { passive: true });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.update);
    }

    this.updateControls();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.handlePrev);
    this.next?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.update);
    this.observer?.disconnect();
    window.removeEventListener('resize', this.update);
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByStep(direction) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    let target = this.track.scrollLeft + this.step * direction;

    if (this.loop) {
      if (direction > 0 && this.track.scrollLeft >= max - 4) target = 0;
      if (direction < 0 && this.track.scrollLeft <= 4) target = max;
    }

    this.track.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  updateControls() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = max > 4;

    this.toggleAttribute('data-scrollable', scrollable);

    if (this.prev) this.prev.disabled = !scrollable || (!this.loop && this.track.scrollLeft <= 4);
    if (this.next) this.next.disabled = !scrollable || (!this.loop && this.track.scrollLeft >= max - 4);
  }
}

if (!customElements.get('js-scroll-strip')) {
  customElements.define('js-scroll-strip', JsScrollStrip);
}
